import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import NotFound from 'secondstep-components/src/components/NotFound'
import { actions as notificationActions } from 'store/notificationsManager'
import TrainingReport from './component'
import {
  TRAINING_REPORT_SLUG_CORE,
  trainingReportPath,
  trainingReportSlugToVariant,
  trainingReportVariantToSlug,
} from './routes'
import {
  getDefaultSiteForReportSlug,
  getSitesWithTrainingAccess,
  getTrainingReportProgramOptions,
  isValidTrainingReportSlug,
  loadSiteTrainingData,
  redirectDwbSlugToCoreWhenSiteLacksAccess,
  redirectInvalidSlugToCore,
  redirectSlugOnlyRouteToDefaultSite,
  siteHasCoreTrainingProgram,
  siteHasDwbTrainingProgram,
} from './helpers'
import {
  VARIANT_K12_DWB,
  VARIANT_K8_CORE,
} from 'components/SiteUserTrainingGrid/constants'
import {
  operations as sitesOperations,
  selectors as sitesSelectors,
} from 'store/sitesManager'
import { operations as lmsOperations } from 'store/lmsManager'

export const TrainingReportContainer = props => {
  const {
    data,
    error,
    fetchSiteUsers,
    fetchTrainingCompletion,
    history,
    isLoading,
    location,
    match,
    pushTo,
    site,
    siteId,
    siteUsers,
    sites,
  } = props

  const reportSlug = match.params.reportSlug
  const { siteId: siteIdParam } = match.params
  const hasSiteIdParam = siteIdParam !== undefined && siteIdParam !== ''
  const isSlugOnlyRoute = !hasSiteIdParam
  const hasInvalidSiteIdParam =
    hasSiteIdParam && Number.isNaN(parseInt(siteIdParam, 10))

  const [reportVariant, setReportVariant] = useState(() =>
    trainingReportSlugToVariant(reportSlug),
  )

  useEffect(() => {
    setReportVariant(trainingReportSlugToVariant(reportSlug))
  }, [reportSlug])

  useEffect(() => {
    redirectInvalidSlugToCore({
      history,
      isSlugOnlyRoute,
      reportSlug,
      siteId,
    })
  }, [siteId, reportSlug, history, isSlugOnlyRoute])

  useEffect(() => {
    redirectDwbSlugToCoreWhenSiteLacksAccess({
      history,
      isLoading,
      isSlugOnlyRoute,
      reportSlug,
      site,
      siteId,
    })
  }, [site, siteId, reportSlug, history, isLoading, isSlugOnlyRoute])

  useEffect(() => {
    redirectSlugOnlyRouteToDefaultSite({
      history,
      isLoading,
      isSlugOnlyRoute,
      reportSlug,
      sites,
    })
  }, [history, isLoading, isSlugOnlyRoute, reportSlug, sites])

  useEffect(() => {
    loadSiteTrainingData({
      fetchSiteUsers,
      fetchTrainingCompletion,
      isSlugOnlyRoute,
      site,
      siteId,
      siteUsers,
    })
  }, [
    siteId,
    isSlugOnlyRoute,
    site,
    siteUsers,
    fetchSiteUsers,
    fetchTrainingCompletion,
  ])

  const pendingUsersCount = siteUsers?.filter(
    user => user?.status === 'Invited',
  ).length
  const activeUsers = siteUsers?.filter(user => user?.status === 'Active')

  const redirectToUserManagement = () => {
    pushTo(`/user-management/${siteId}`)
  }

  const onReportVariantChange = useCallback(
    variant => {
      if (variant === VARIANT_K12_DWB && !siteHasDwbTrainingProgram(site)) {
        return
      }
      setReportVariant(variant)
      if (!siteId || Number.isNaN(siteId)) {
        return
      }
      pushTo(trainingReportPath(trainingReportVariantToSlug(variant), siteId))
    },
    [siteId, pushTo, site],
  )

  const onSiteChange = e => {
    const { value } = e
    const newSiteId = value.id || {}
    const isDifferentSite = siteId !== newSiteId

    if (isDifferentSite) {
      const hasDwb = siteHasDwbTrainingProgram(value)
      const hasCore = siteHasCoreTrainingProgram(value)
      let nextVariant = reportVariant

      if (!hasCore && hasDwb) {
        nextVariant = VARIANT_K12_DWB
      } else if (!hasDwb && nextVariant === VARIANT_K12_DWB) {
        nextVariant = VARIANT_K8_CORE
      }

      const slug = hasDwb
        ? trainingReportVariantToSlug(nextVariant)
        : TRAINING_REPORT_SLUG_CORE

      pushTo(trainingReportPath(slug, newSiteId))

      if (nextVariant !== reportVariant) {
        setReportVariant(nextVariant)
      }
    }
  }

  const k8Sites = getSitesWithTrainingAccess(sites)
  const programOptions = getTrainingReportProgramOptions(site)
  const showProgramSelect = programOptions.length > 1

  if (isSlugOnlyRoute) {
    if (isLoading) {
      return (
        <TrainingReport
          isLoading
          onReportVariantChange={onReportVariantChange}
          reportVariant={reportVariant}
        />
      )
    }
    if (!isValidTrainingReportSlug(reportSlug)) {
      return <NotFound location={location} />
    }
    if (!getDefaultSiteForReportSlug(sites, reportSlug)) {
      return <NotFound location={location} />
    }
    return (
      <TrainingReport
        isLoading
        onReportVariantChange={onReportVariantChange}
        reportVariant={reportVariant}
      />
    )
  }

  if (!isLoading && (hasInvalidSiteIdParam || !site)) {
    return <NotFound location={location} />
  }

  return (
    <TrainingReport
      data={data}
      error={error}
      isLoading={isLoading}
      location={location}
      onReportVariantChange={onReportVariantChange}
      onSiteChange={onSiteChange}
      pendingUsersCount={pendingUsersCount}
      programOptions={programOptions}
      redirectToUserManagement={redirectToUserManagement}
      reportVariant={reportVariant}
      showProgramSelect={showProgramSelect}
      site={site}
      siteId={siteId}
      siteUsers={activeUsers}
      sites={k8Sites}
    />
  )
}

TrainingReportContainer.propTypes = {
  addNotification: PropTypes.func.isRequired,
  data: PropTypes.array,
  error: PropTypes.object,
  fetchSiteUsers: PropTypes.func.isRequired,
  fetchTrainingCompletion: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    query: PropTypes.object,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportSlug: PropTypes.string,
      siteId: PropTypes.string,
    }),
  }).isRequired,
  message: PropTypes.string,
  pushTo: PropTypes.func.isRequired,
  site: PropTypes.object,
  siteId: PropTypes.number,
  siteUsers: PropTypes.array,
  sites: PropTypes.array,
}

TrainingReportContainer.defaultProps = {
  isLoading: false,
  data: [],
  error: null,
  message: '',
}

const mapStateToProps = (state, { match }) => {
  const {
    router,
    sitesManager: {
      message,
      sites: { data: sites, isLoading: isSitesLoading },
      siteUsers: { isLoading: isSiteUsersLoading },
    },
    lmsManager: { isFetching: isTrainingCompletionLoading },
  } = state

  const isLoading =
    isSitesLoading || isSiteUsersLoading || isTrainingCompletionLoading
  const { siteId: siteIdParam } = match.params
  const siteId = siteIdParam ? parseInt(siteIdParam, 10) : undefined
  const site =
    siteId && !Number.isNaN(siteId)
      ? sites?.find(site => site.id === siteId)
      : undefined

  const siteUsers = sitesSelectors.selectSiteUsersWithTrainingCompletionBySiteId(
    state,
    siteId,
  )

  const { location } = router

  return {
    isLoading,
    location,
    message,
    site,
    siteId,
    sites,
    siteUsers,
  }
}

const mapDispatchToProps = {
  addNotification: notificationActions.addNotification,
  fetchSiteUsers: sitesOperations.fetchSiteUsers,
  fetchTrainingCompletion: lmsOperations.fetchTrainingCompletion,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TrainingReportContainer)
